import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "../common/NotificationContext";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faChevronDown,
  faChevronUp,
  faBox,
  faMars,
  faVenus,
} from "@fortawesome/free-solid-svg-icons";
import { InputField } from "../common/InputField";
import { motion, AnimatePresence } from "framer-motion";

export const RPCS = () => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [state, setState] = useState({
    filteredParticipants: [],
    searchQuery: "",
    loading: true,
    totalFiltered: 0,
    allParticipants: [],
    isAdminOrRpc: true,
    greeting: "",
    userName: "",
    showAll: false,
    locationCounts: {
      "Total": { total: 0, collected: 0, notCollected: 0 },
    },
  });
  const [expandedParticipantId, setExpandedParticipantId] = useState(null);
  const [delegateNames, setDelegateNames] = useState({});
  const [delegateSearchQueries, setDelegateSearchQueries] = useState({});
  const navigate = useNavigate();
  const isMounted = useRef(true);

  // Determine greeting based on time
  useEffect(() => {
    const hour = new Date().getHours();
    let greeting = "";
    if (hour < 12) greeting = "Good Morning";
    else if (hour < 15) greeting = "Good Afternoon";
    else if (hour < 18) greeting = "Good Evening";
    else greeting = "Good Evening";
    setState((prev) => ({ ...prev, greeting }));
  }, []);

  // Get user name
  useEffect(() => {
    if (user && user.displayName) {
      setState((prev) => ({ ...prev, userName: user.displayName }));
    } else {
      setState((prev) => ({ ...prev, userName: "BMM Participant" }));
    }
  }, [user]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Fetch all participants - PUBLIC ACCESS
  const fetchAllParticipants = useCallback(async () => {
    try {
      // Call backend API (no authentication needed for viewing)
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/api/rpcs/participants/bmm`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      const allParticipants = result.participants || [];
      
      allParticipants.sort((a, b) => a.nama.localeCompare(b.nama));

      // Calculate total counts
      const counts = allParticipants.reduce(
        (acc, p) => {
          acc["Total"].total += 1;
          if (p.racePackCollected) {
            acc["Total"].collected += 1;
          } else {
            acc["Total"].notCollected += 1;
          }
          return acc;
        },
        {
          "Total": { total: 0, collected: 0, notCollected: 0 },
        }
      );

      if (isMounted.current) {
        setState((prev) => ({
          ...prev,
          allParticipants,
          filteredParticipants: [],
          totalFiltered: 0,
          locationCounts: counts,
          loading: false,
        }));
      }
    } catch (err) {
      console.error("[ERROR] Fetching participants:", err);
      if (isMounted.current) {
        setState((prev) => ({ ...prev, loading: false }));
        showNotification(
          "error",
          "Failed to load participants. Please try again."
        );
      }
    }
  }, [user, showNotification]);

  // Handle search and sorting
  useEffect(() => {
    let filtered = [...state.allParticipants];

    if (state.searchQuery && state.isAdminOrRpc) {
      const lowerQuery = state.searchQuery.toLowerCase().trim();
      filtered = state.allParticipants.filter((p) => {
        const mainFields = [
          p.namaLengkap,
          p.teamName,
          p.email,
          p.orderId,
          ...(Array.isArray(p.bibNumber) ? p.bibNumber : [p.bibNumber]),
        ].filter(Boolean);
        if (
          mainFields.some((field) => field.toLowerCase().includes(lowerQuery))
        ) {
          return true;
        }
        return false;
      });
    } else if (!state.showAll && !state.searchQuery) {
      filtered = [];
    }

    if (isMounted.current) {
      setState((prev) => ({
        ...prev,
        filteredParticipants: filtered,
        totalFiltered: filtered.length,
      }));
    }
  }, [
    state.searchQuery,
    state.allParticipants,
    state.isAdminOrRpc,
    state.showAll,
  ]);

  // Initial data fetch
  useEffect(() => {
    if (state.isAdminOrRpc) {
      fetchAllParticipants();
    }
  }, [fetchAllParticipants, state.isAdminOrRpc]);

  // Handle search input change
  const handleSearchChange = (e) => {
    setState((prev) => ({
      ...prev,
      searchQuery: e.target.value,
      showAll: false,
    }));
    setExpandedParticipantId(null);
  };

  // Handle show all participants toggle
  const handleToggleShowAll = () => {
    setState((prev) => ({ ...prev, showAll: !prev.showAll, searchQuery: "" }));
    setExpandedParticipantId(null);
  };

  // Handle delegate name change
  const handleDelegateNameChange = (id, value) => {
    setDelegateNames((prev) => ({ ...prev, [id]: value }));
    setDelegateSearchQueries((prev) => ({ ...prev, [id]: "" }));
  };

  // Handle delegate search query change
  const handleDelegateSearchChange = (id, value) => {
    setDelegateSearchQueries((prev) => ({ ...prev, [id]: value }));

    const matchedParticipant = state.allParticipants.find((p) =>
      p.namaLengkap.toLowerCase().startsWith(value.toLowerCase())
    );

    if (
      matchedParticipant &&
      value &&
      matchedParticipant.namaLengkap
        .toLowerCase()
        .startsWith(value.toLowerCase())
    ) {
      setDelegateNames((prev) => ({
        ...prev,
        [id]: matchedParticipant.namaLengkap,
      }));
    } else {
      setDelegateNames((prev) => ({ ...prev, [id]: value }));
    }
  };

  // Toggle racePackCollected status
  const handleToggleRacePack = async (participant) => {
    const newStatus = !participant.racePackCollected;
    const delegateName = delegateNames[participant.id] || "";
    const participantId = participant.id;
    
    // OPTIMISTIC UPDATE - Update UI immediately for instant feedback
    setState((prev) => {
      const currentParticipant = prev.allParticipants.find(p => p.id === participantId);
      const oldStatus = currentParticipant?.racePackCollected || false;
      
      if (oldStatus === newStatus) {
        return prev;
      }
      
      const newCounts = {
        ...prev.locationCounts,
        "Total": { ...prev.locationCounts["Total"] }
      };
      
      if (newStatus && !oldStatus) {
         newCounts["Total"].collected += 1;
         newCounts["Total"].notCollected -= 1;
      } else if (!newStatus && oldStatus) {
         newCounts["Total"].collected -= 1;
         newCounts["Total"].notCollected += 1;
      }
      
      return {
        ...prev,
        allParticipants: prev.allParticipants.map((p) =>
          p.id === participantId
            ? {
                ...p,
                racePackCollected: newStatus,
                rpcDelegateName: newStatus ? delegateName : "",
              }
            : p
        ),
        filteredParticipants: prev.filteredParticipants.map((p) =>
          p.id === participantId
            ? {
                ...p,
                racePackCollected: newStatus,
                rpcDelegateName: newStatus ? delegateName : "",
              }
            : p
        ),
        locationCounts: newCounts,
      };
    });
    
    // Clear delegate names when undoing
    if (!newStatus) {
      setDelegateNames((prev) => ({ ...prev, [participantId]: "" }));
      setDelegateSearchQueries((prev) => ({ ...prev, [participantId]: "" }));
    }
    
    // Auto-close detail view
    setExpandedParticipantId(null);
    
    // THEN sync with backend in background
    try {
      // Get Firebase ID token for authentication
      const token = await user.getIdToken();
      
      // Call backend API to update status
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/api/rpcs/participants/bmm/${participantId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            racePackCollected: newStatus,
            rpcDelegateName: newStatus ? delegateName : "",
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }
      
      console.log("[RPCS] Successfully synced with backend");
    } catch (err) {
      console.error("[ERROR] Backend sync failed:", err);
      // Rollback UI on error
      setState((prev) => ({
        ...prev,
        allParticipants: prev.allParticipants.map((p) =>
          p.id === participantId
            ? { ...p, racePackCollected: !newStatus, rpcDelegateName: "" }
            : p
        ),
        filteredParticipants: prev.filteredParticipants.map((p) =>
          p.id === participantId
            ? { ...p, racePackCollected: !newStatus, rpcDelegateName: "" }
            : p
        ),
      }));
      
      showNotification(
        "error",
        "Failed to update status: " + err.message
      );
    }
  };

  const handleExpand = (id) => {
    setExpandedParticipantId((prevId) => (prevId === id ? null : id));
  };

  const formatWhatsAppNumber = (phone) => {
    if (!phone) return null;
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.startsWith("0")) {
      return `+62${cleaned.slice(1)}`;
    }
    return cleaned;
  };

  const rangeText = `Showing ${state.filteredParticipants.length} of ${state.totalFiltered} Participants`;

  if (!state.isAdminOrRpc) return null;

  return (
    <section
      id="rpc-system"
      className="w-full min-h-screen py-20 text-slate-700 flex-col justify-center relative bg-white"
    >
      <div className="relative z-0 container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          {/* Page Title */}
          <div className="text-center mb-8">
            <motion.h2
              className="text-2xl font-black uppercase tracking-wider text-slate-700"
              initial={{ opacity: 0, y: -15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              RACE PACK COLLECTION
            </motion.h2>
            <motion.h3
              className="text-3xl md:text-5xl font-black text-slate-700 mt-2"
              initial={{ opacity: 0, y: -15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
            >
              BALI MOUNTAIN MARATHON
            </motion.h3>
          </div>

          {/* Stats Container - Added back but with simple styling */}
          <motion.div
            className="bg-white rounded-2xl border border-gray-200 p-4 mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            viewport={{ once: true }}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-sm text-gray-500 font-semibold mb-1">
                  Total Participants
                </p>
                <p className="text-2xl font-bold text-slate-700">
                  {state.locationCounts["Total"].total}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 font-semibold mb-1">
                  Collected
                </p>
                <p className="text-2xl font-bold text-emerald-600">
                  {state.locationCounts["Total"].collected}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 font-semibold mb-1">
                  Not Collected
                </p>
                <p className="text-2xl font-bold text-red-600">
                  {state.locationCounts["Total"].notCollected}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Greeting and Search Section - Restored Layout */}
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            viewport={{ once: true }}
          >
            <div className="bg-white rounded-2xl border border-gray-200 p-4 text-center">
              <p className="text-base text-gray-600">{state.greeting}</p>
              <p className="text-2xl font-bold text-slate-600 uppercase mt-2">
                {state.userName}
              </p>
              <p className="text-sm bg-slate-50 border border-slate-400 rounded-xl text-slate-600 mt-2 px-4 py-1 inline-block">
                BMM Race Pack Collection Crew
              </p>
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 p-4 text-center">
              <p className="text-base text-gray-600 font-medium mb-2">
                Search Participant
              </p>
              <div className="w-full max-w-xs mx-auto">
                <InputField
                  name="search"
                  type="text"
                  value={state.searchQuery}
                  onChange={handleSearchChange}
                  placeholder="Search by name, group, BIB number..."
                  aria-label="Search participant"
                  inputClassName="input w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-slate-600 text-gray-800 text-base"
                  startIcon={
                    <FontAwesomeIcon
                      icon={faSearch}
                      className="text-gray-400"
                    />
                  }
                />
              </div>
              <button
                onClick={handleToggleShowAll}
                className="text-slate-600 hover:underline text-base mt-2"
              >
                {state.showAll
                  ? "Hide Participants"
                  : "Show All Participants"}
              </button>
            </div>
          </motion.div>

          {/* Search Results Section */}
          <motion.div
            className="bg-white rounded-2xl border border-gray-200"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            viewport={{ once: true }}
          >
            <div className="space-y-0">
              {state.loading ? (
                <div className="text-left text-base text-gray-600 p-4">
                  Loading data...
                </div>
              ) : !state.searchQuery && !state.showAll ? (
                <div className="text-center text-base text-gray-600 p-4">
                  Please enter a name, group name, or BIB number to search or click "Show All Participants".
                </div>
              ) : state.filteredParticipants.length === 0 ? (
                <div className="text-left text-base text-gray-600 p-4">
                  No participants match your search.
                </div>
              ) : (
                <div>
                  <div className="grid grid-cols-6 gap-2 items-center px-4 py-4 bg-slate-50 rounded-t-2xl text-slate-600 font-semibold text-sm">
                    <span className="text-left">Name</span>
                    <span className="text-center">Nationality</span>
                    <span className="text-center">BIB</span>
                    <span className="text-center">Gender</span>
                    <span className="text-left">Category</span>
                    <span className="text-left">Status</span>
                  </div>
                  {state.filteredParticipants.map((p) => (
                    <motion.div
                      key={p.id}
                      className="overflow-hidden bg-white"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <button
                        className="w-full grid grid-cols-6 gap-2 items-center px-4 py-4 text-sm border-t border-gray-200 hover:bg-slate-50"
                        onClick={() => handleExpand(p.id)}
                      >
                        <span className="text-gray-800 text-left truncate">
                          {p.nama ? p.nama.toUpperCase() : "-"}
                        </span>
                        <span className="flex justify-center">
                          <span className="text-slate-600 text-xs font-bold tracking-wider">
                            {(() => {
                              const nat = (p.nationality || "Indonesia").toLowerCase();
                              if (nat === "indonesia") return "INA";
                              if (nat === "korea" || nat === "south korea") return "KOR";
                              if (nat === "united states" || nat === "usa" || nat === "us") return "USA";
                              if (nat === "australia") return "AUS";
                              if (nat === "japan") return "JPN";
                              if (nat === "united kingdom" || nat === "uk" || nat === "great britain") return "GBR";
                              if (nat === "singapore") return "SGP";
                              if (nat === "malaysia") return "MAS";
                              if (nat === "china") return "CHN";
                              if (nat === "india") return "IND";
                              if (nat === "netherlands") return "NED";
                              if (nat === "germany") return "GER";
                              if (nat === "france") return "FRA";
                              if (nat === "thailand") return "THA";
                              if (nat === "philippines") return "PHI";
                              if (nat === "vietnam") return "VIE";
                              if (nat === "new zealand") return "NZL";
                              if (nat === "canada") return "CAN";
                              return p.nationality?.substring(0, 3).toUpperCase() || "INA";
                            })()}
                          </span>
                        </span>
                        <span className="flex justify-center">
                          {p.bibNumber[0] ? (
                            <span className="inline-flex items-center px-3 py-1 rounded-full border border-gray-300 bg-white text-slate-700 text-xs font-medium">
                              BIB {p.bibNumber[0]}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </span>
                        <span className="flex justify-center">
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-gray-300 bg-white text-slate-600 text-xs font-medium">
                            <FontAwesomeIcon 
                              icon={p.gender === "Male" ? faMars : faVenus} 
                              className="text-slate-500"
                            />
                            <span>{p.gender || "-"}</span>
                          </span>
                        </span>
                        <span className="text-gray-500 text-left truncate">
                          {p.kategori || "-"}
                        </span>
                        <div className="flex justify-start items-center gap-2">
                          <span
                            className={`px-2 py-0.5 rounded-lg text-sm ${
                              p.racePackCollected
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {p.racePackCollected ? "Collected" : "Not Collected"}
                          </span>
                          <FontAwesomeIcon
                            icon={
                              expandedParticipantId === p.id
                                ? faChevronUp
                                : faChevronDown
                            }
                            className="text-gray-500"
                          />
                        </div>
                      </button>
                      {expandedParticipantId === p.id && (
                        <div className="p-4 bg-slate-50 border-t border-gray-100">
                          {/* Status Banner */}
                          <div className={`rounded-xl p-4 mb-4 flex items-start gap-4 ${
                            p.transaction_status === "settlement" 
                              ? "bg-blue-50 border border-blue-100" 
                              : "bg-red-50 border border-red-100"
                          }`}>
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                              p.transaction_status === "settlement" ? "bg-blue-100 text-blue-600" : "bg-red-100 text-red-600"
                            }`}>
                              <FontAwesomeIcon icon={faBox} className="text-lg" />
                            </div>
                            <div>
                              <h4 className={`text-lg font-bold ${
                                p.transaction_status === "settlement" ? "text-blue-700" : "text-red-700"
                              }`}>
                                {p.transaction_status === "settlement" ? "READY TO COLLECT" : "PAYMENT ISSUE"}
                              </h4>
                              <p className={`text-sm font-medium ${
                                p.transaction_status === "settlement" ? "text-blue-600" : "text-red-600"
                              }`}>
                                {p.transaction_status === "settlement" 
                                  ? "Verified & Payment Valid" 
                                  : `Payment Status: ${p.transaction_status}`}
                              </p>
                            </div>
                          </div>

                          {/* Main Details Card */}
                          <div className="bg-white border border-gray-200 rounded-2xl p-0 overflow-hidden">
                            <div className="grid grid-cols-1 md:grid-cols-[1fr_1px_1fr] lg:grid-cols-[1.5fr_1px_1fr]">
                              {/* Left Section: Participant Info */}
                              <div className="p-4 md:p-8 space-y-4">
                                <div>
                                  <span className="block text-xs font-bold text-gray-400 tracking-wider uppercase mb-1">
                                    PARTICIPANT
                                  </span>
                                  <h3 className="text-xl md:text-2xl font-black text-slate-800 leading-tight mb-1">
                                    {p.namaLengkap || p.nama}
                                  </h3>
                                  <p className="text-slate-500 font-medium text-sm">
                                    {p.email}
                                  </p>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <span className="block text-xs font-bold text-gray-400 tracking-wider uppercase mb-1">
                                      CATEGORY
                                    </span>
                                    <p className="text-lg font-bold text-slate-800">
                                      {p.kategori || "-"}
                                    </p>
                                  </div>
                                  <div>
                                    <span className="block text-xs font-bold text-gray-400 tracking-wider uppercase mb-1">
                                      GENDER
                                    </span>
                                    <p className="text-lg font-bold text-slate-800">
                                      {p.gender || "-"}
                                    </p>
                                  </div>
                                  <div className="mt-2">
                                     <span className="block text-xs font-bold text-gray-400 tracking-wider uppercase mb-1">
                                      ORDER ID
                                    </span>
                                    <p className="text-sm font-medium text-slate-700">
                                      {p.orderId || "-"}
                                    </p>
                                  </div>
                                  <div className="mt-2">
                                     <span className="block text-xs font-bold text-gray-400 tracking-wider uppercase mb-1">
                                      PHONE
                                    </span>
                                    <p className="text-sm font-medium text-slate-700">
                                      {p.telepon || "-"}
                                    </p>
                                  </div>
                                </div>
                              </div>

                              {/* Vertical Divider (Hidden on mobile) */}
                              <div className="hidden md:block bg-gray-100 h-full w-[1px]"></div>

                              {/* Right Section: Jersey & Bib */}
                              <div className="p-6 md:p-8 bg-slate-50/50 space-y-8 flex flex-col justify-center">
                                <div>
                                  <span className="block text-xs font-bold text-gray-400 tracking-wider uppercase mb-2">
                                    JERSEY SIZE
                                  </span>
                                  <p className="text-5xl font-black text-slate-800 tracking-tight">
                                    {p.sizeJersey[0] || "-"}
                                  </p>
                                </div>
                                <div>
                                   <span className="block text-xs font-bold text-gray-400 tracking-wider uppercase mb-2">
                                    BIB NUMBER
                                  </span>
                                   <p className="text-4xl font-black text-slate-800 tracking-widest font-mono">
                                    {p.bibNumber[0] || "---"}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>

                           {/* Representative Section (if needed) */}
                           {!p.racePackCollected && (
                              <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4"> 
                                  <InputField
                                    label="Representative Name (if applicable)"
                                    name={`delegate_${p.id}`}
                                    value={delegateNames[p.id] || ""}
                                    onChange={(e) => handleDelegateNameChange(p.id, e.target.value)}
                                    placeholder="Enter representative name..."
                                    inputClassName="w-full bg-white px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-slate-800 transition-all outline-none"
                                  />
                                   <InputField
                                        label="Search Representative (Participant)"
                                        name={`delegate_search_${p.id}`}
                                        value={delegateSearchQueries[p.id] || ""}
                                        onChange={(e) =>
                                          handleDelegateSearchChange(
                                            p.id,
                                            e.target.value
                                          )
                                        }
                                        placeholder="Search by name..."
                                        inputClassName="w-full bg-white px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-slate-800 transition-all outline-none"
                                  />
                              </div>
                           )}

                           {/* Representative Display (if collected) */}
                           {p.racePackCollected && p.rpcDelegateName && (
                              <div className="mt-4 p-4 bg-white border border-gray-200 rounded-xl">
                                  <span className="text-xs font-bold text-gray-400 uppercase tracking-wider block mb-1">
                                    Collected By Representative
                                  </span>
                                  <p className="text-slate-800 font-bold">{p.rpcDelegateName}</p>
                              </div>
                           )}

                          {/* Action Bar */}
                          <div className="flex flex-col md:flex-row gap-4 items-center pt-4">
                            <button
                              onClick={() => handleExpand(p.id)}
                              className="w-full md:w-auto px-6 py-4 rounded-xl border border-gray-300 bg-white text-slate-700 font-bold uppercase tracking-wider text-sm hover:bg-gray-50 transition-colors"
                            >
                              Cancel / Scan Next
                            </button>
                            <button
                               onClick={() => handleToggleRacePack(p)}
                               disabled={state.loading}
                               className={`w-full md:flex-1 px-6 py-4 rounded-xl font-bold uppercase tracking-wider text-sm transition-colors ${
                                 p.racePackCollected
                                   ? "bg-red-600 text-white hover:bg-red-700"
                                   : "bg-slate-800 text-white hover:bg-slate-900"
                               }`}
                            >
                              {p.racePackCollected ? "Undo Collection" : "CONFIRM COLLECTION"}
                            </button>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
            {state.filteredParticipants.length > 0 && (
              <div className="flex justify-end items-center px-4 py-4">
                <span className="text-base text-gray-600">{rangeText}</span>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
